import React, { useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Activity, ArrowLeft, MailCheck } from "lucide-react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/password/forgot", { email, origin: window.location.origin });
      setSent(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send reset email");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
      <div className="w-full max-w-md" data-testid="forgot-password-page">
        <Link to="/login" className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900 mb-6" data-testid="back-login">
          <ArrowLeft className="w-4 h-4" /> Back to sign in
        </Link>
        <div className="flex items-center gap-2 mb-6">
          <div className="w-10 h-10 rounded-md bg-[#0B5FFF] text-white flex items-center justify-center">
            <Activity className="w-5 h-5" />
          </div>
          <div>
            <div className="font-heading font-extrabold text-[#111827]">RapidCare SA</div>
            <div className="text-[10px] tracking-widest text-slate-500 uppercase">Password recovery</div>
          </div>
        </div>
        <Card className="border-slate-200">
          <CardContent className="p-6">
            {sent ? (
              <div className="text-center py-6" data-testid="forgot-sent-state">
                <div className="w-14 h-14 rounded-full bg-emerald-50 text-emerald-700 flex items-center justify-center mx-auto mb-4">
                  <MailCheck className="w-7 h-7" />
                </div>
                <h1 className="font-heading text-2xl font-bold text-[#111827]">Check your inbox</h1>
                <p className="text-sm text-slate-600 mt-2">
                  If <span className="font-medium">{email}</span> is registered with RapidCare SA, we've sent a password reset link. It expires in 30 minutes.
                </p>
                <p className="text-xs text-slate-500 mt-4">Didn't get it? Check spam, or try again in a minute.</p>
                <Link to="/login">
                  <Button className="mt-6 w-full bg-[#0B5FFF] hover:bg-[#0A54E0] h-11" data-testid="back-to-login-btn">
                    Back to sign in
                  </Button>
                </Link>
              </div>
            ) : (
              <>
                <h1 className="font-heading text-2xl font-bold text-[#111827]">Forgot your password?</h1>
                <p className="text-sm text-slate-600 mt-2">Enter the email you used to register and we'll send a secure reset link.</p>
                <form onSubmit={submit} className="space-y-4 mt-6">
                  <div>
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@clinic.co.za"
                      className="mt-1.5"
                      data-testid="forgot-email-input"
                    />
                  </div>
                  <Button type="submit" disabled={loading} className="w-full bg-[#0B5FFF] hover:bg-[#0A54E0] h-11" data-testid="forgot-submit">
                    {loading ? "Sending..." : "Send reset link"}
                  </Button>
                </form>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
